import { type IsoDate, addMonths, compareDates } from './dates';
import { CHECKPOINT_DONE_STATUSES, type CheckpointStatus, type TrafficLight } from './enums';

/**
 * Painéis: as atividades do plano agrupadas pela situação que importa a quem acompanha — concluídas, enviadas
 * (aguardando avaliação), com ajustes pedidos, no prazo ou atrasadas — e distribuídas pelo mês do prazo.
 */
export type ActivityBucket = 'done' | 'awaiting' | 'changes' | 'open' | 'late';
export const ACTIVITY_BUCKETS: ActivityBucket[] = ['done', 'awaiting', 'changes', 'open', 'late'];
export const ACTIVITY_BUCKET_LABELS: Record<ActivityBucket, string> = {
  done: 'Concluídas',
  awaiting: 'Aguardando avaliação',
  changes: 'Ajustes solicitados',
  open: 'No prazo',
  late: 'Atrasadas',
};

/**
 * Semáforo do prazo de uma atividade em aberto: quanto mais perto do vencimento e mais itens obrigatórios
 * pendentes, mais grave. Vermelho: atrasada ou vence em até 7 dias com pendência. Amarelo: vence em até
 * 30 dias com metade ou mais dos itens pendentes. Verde: o restante (prazo folgado ou quase tudo pronto).
 */
export function deadlineSignal(daysLeft: number, done: number, total: number): TrafficLight {
  if (daysLeft < 0) return 'RED';
  // Sem itens obrigatórios, tudo depende de enviar a atividade: conta como pendente.
  const pending = total > 0 ? (total - done) / total : 1;
  if (pending === 0) return 'GREEN';
  if (daysLeft <= 7) return 'RED';
  if (daysLeft <= 30 && pending >= 0.5) return 'YELLOW';
  return 'GREEN';
}

export interface ActivityLike {
  status: CheckpointStatus;
  dueDate: IsoDate;
}

export type BucketCounts = Record<ActivityBucket, number>;
export const emptyBuckets = (): BucketCounts => ({ done: 0, awaiting: 0, changes: 0, open: 0, late: 0 });

/** Situação de uma atividade no painel (dispensadas contam como concluídas). */
export function activityBucket(a: ActivityLike, today: IsoDate): ActivityBucket {
  if (CHECKPOINT_DONE_STATUSES.includes(a.status)) return 'done';
  if (a.status === 'SUBMITTED') return 'awaiting';
  if (compareDates(a.dueDate, today) < 0) return 'late';
  return a.status === 'CHANGES_REQUESTED' ? 'changes' : 'open';
}

export function countBuckets(items: ActivityLike[], today: IsoDate): BucketCounts {
  const out = emptyBuckets();
  for (const a of items) out[activityBucket(a, today)]++;
  return out;
}

/** Situação das atividades separada por uma chave (ex.: o plano de cada orientando(a)). */
export function countBucketsBy<T extends ActivityLike>(items: T[], key: (a: T) => string, today: IsoDate): Record<string, BucketCounts> {
  const out: Record<string, BucketCounts> = {};
  for (const a of items) (out[key(a)] ??= emptyBuckets())[activityBucket(a, today)]++;
  return out;
}

/** Mês no formato AAAA-MM. */
export const monthKey = (date: IsoDate) => date.slice(0, 7);

const MONTHS_PT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
/** Rótulo curto do mês (ex.: "out/26"). */
export function monthLabel(ym: string): string {
  const [y, m] = ym.split('-');
  return `${MONTHS_PT[Number(m) - 1] ?? m}/${(y ?? '').slice(2)}`;
}

/** Meses consecutivos (AAAA-MM) em torno de hoje: `before` para trás e `after` para a frente. */
export function monthRange(today: IsoDate, before: number, after: number): string[] {
  const first = `${monthKey(today)}-01`;
  return Array.from({ length: before + after + 1 }, (_, i) => monthKey(addMonths(first, i - before)));
}

export interface MonthBucket extends BucketCounts {
  month: string;
  label: string;
  total: number;
}

/** Atividades por mês do prazo (meses sem nada ficam zerados; fora da janela são ignorados). */
export function bucketByMonth(items: ActivityLike[], months: string[], today: IsoDate): MonthBucket[] {
  const map = new Map(months.map((month) => [month, { month, label: monthLabel(month), total: 0, ...emptyBuckets() }]));
  for (const a of items) {
    const b = map.get(monthKey(a.dueDate));
    if (!b) continue;
    b[activityBucket(a, today)]++;
    b.total++;
  }
  return [...map.values()];
}

/** Contagem por mês de criação (AAAA-MM) de uma lista de datas, na janela dada. */
export function countByMonth(dates: IsoDate[], months: string[]): { month: string; label: string; count: number }[] {
  const map = new Map(months.map((month) => [month, { month, label: monthLabel(month), count: 0 }]));
  for (const d of dates) {
    const b = map.get(monthKey(d));
    if (b) b.count++;
  }
  return [...map.values()];
}

export interface GroupCount {
  group: string;
  total: number;
  done: number;
}

/** Total e concluídas por rótulo (modelo de origem ou rótulo livre), dos maiores para os menores. */
export function countByGroup(items: { status: CheckpointStatus; group: string | null }[], fallback = 'Sem rótulo'): GroupCount[] {
  const groups = new Map<string, GroupCount>();
  for (const a of items) {
    const key = a.group?.trim() || fallback;
    const g = groups.get(key) ?? { group: key, total: 0, done: 0 };
    g.total++;
    if (a.status === 'APPROVED') g.done++;
    groups.set(key, g);
  }
  return [...groups.values()].sort((a, b) => b.total - a.total || a.group.localeCompare(b.group));
}

/** Janela dos gráficos por mês: 2 meses para trás e 9 para a frente. */
export const CHART_MONTHS = { before: 2, after: 9 } as const;

export interface ActivityCharts {
  /** Situação de todas as atividades (sem sugestões pendentes nem dispensadas) */
  byStatus: BucketCounts;
  /** Atividades por mês do prazo, na janela CHART_MONTHS */
  byMonth: MonthBucket[];
  /** Por rótulo: total e concluídas (os 8 maiores) */
  byGroup: GroupCount[];
}

/** Gráficos do painel da orientação: os mesmos três, mais a situação separada por plano (um por orientando(a)). */
export interface PlanActivityCharts extends ActivityCharts {
  /** Situação das atividades por plano (`AdvisingLink.planWorkId`) */
  byPlan: Record<string, BucketCounts>;
}

/** Os três gráficos de atividades, a partir das mesmas linhas (planos de um(a) orientador(a), de um plano ou da plataforma). */
export function activityCharts(items: (ActivityLike & { group: string | null })[], today: IsoDate): ActivityCharts {
  return {
    byStatus: countBuckets(items, today),
    byMonth: bucketByMonth(items, monthRange(today, CHART_MONTHS.before, CHART_MONTHS.after), today),
    byGroup: countByGroup(items).slice(0, 8),
  };
}
